/* eslint-disable tsdoc/syntax -- ignore doc syntax for file */

import {
    type ApolloClient,
    type ApolloQueryResult,
    type NormalizedCacheObject,
    type OperationVariables,
    type QueryOptions,
} from '@apollo/client';
import type {
    IServerSideDatasource,
    IServerSideGetRowsParams,
    IServerSideGetRowsRequest,
    LoadSuccessParams,
    SortModelItem,
} from '@ag-grid-community/core';
import apolloClient from '@api/client';
import { OrderDirection } from '@gql/graphql';
import {
    convertFilterModelToGraphQLWhereStatement,
    type FilterModels,
    mapToObjectRec,
} from './graphql-conversion';

export type ServerSideVariables = {
    [key: string]:
        | string
        | number
        | boolean
        | Date
        | ServerSideVariables
        | ServerSideVariables[]
        | string[];
} | null;

interface ServerSideDatasourceStrategy {
    fetchData: (params: IServerSideGetRowsParams) => void;
}

/**
 * This class is a wrapper around the ag-grid datasource interface.
 * It allows us to inject a strategy for fetching data from a server.
 */
export class Datasource implements IServerSideDatasource {
    strategy: ServerSideDatasourceStrategy;

    constructor(strategy: ServerSideDatasourceStrategy) {
        this.strategy = strategy;
    }

    getRows(params: IServerSideGetRowsParams): void {
        this.strategy.fetchData(params);
    }
}

/**
 * Helpers
 */

function mapSortDirection(dir: string): string {
    switch (dir) {
        case 'asc':
            return OrderDirection.Asc;
        case 'desc':
            return OrderDirection.Desc;
        default:
            return OrderDirection.Asc;
    }
}

export interface GraphQLDataSourceOptions
    extends Pick<QueryOptions, 'query' | 'variables'> {
    columnSortMap?: (columnId: string) => string;
    columnFilterMap?: (model: FilterModels) => ServerSideVariables;
    /**
     * If true, the datasource will only fetch the requested amount of data but not more. Essentially disabling pagination.
     */
    hardLimit?: number;
    /**
     * If true, additional logs will be printed to the console. (default: false)
     */
    debug?: boolean;
    /**
     * If true, rowCount is not reported upfront — AG Grid only knows about loaded rows.
     * Rows load progressively as the user scrolls, with a properly-sized scrollbar.
     */
    infiniteScroll?: boolean;
}

export interface GraphQLDataSourceCallbackResult {
    rows: Record<string, string | number | boolean | Date>[] | unknown[];
    totalCount?: number;
}

type GraphQLDataSourceCallback<T> = (
    response: ApolloQueryResult<T>,
    query: QueryOptions<OperationVariables, T>,
) => GraphQLDataSourceCallbackResult;

/**
 * This class is a concrete implementation of the ServerSideDatasourceStrategy interface.
 * It uses the ApolloClient to fetch data from a GraphQL server.
 * You specify the query options and a callback to handle the successful response.
 * The callback is responsible for returning the rows of data to ag-grid.
 *
 * The GraphQL query must accept the following parameters:
 * - first: number (the number of rows to fetch)
 * - orderBy: { field: string, direction: OrderDirection } (the sorting mechanism)
 * - where: { [key: string]: string | number | boolean | Date } (the filtering mechanism)
 */

interface FilterParameters {
    first?: number;
    orderBy?: { field: string; direction: string };
    where?: ServerSideVariables;
}

export class GraphQLDataSourceStrategy<T>
    implements ServerSideDatasourceStrategy
{
    // The ApolloClient instance
    client: ApolloClient<NormalizedCacheObject>;
    // The set of options that applies to the query and other things
    options: GraphQLDataSourceOptions;
    // The callback that is responsible for handling the response and returning the data to ag-grid
    callback: GraphQLDataSourceCallback<T>;

    constructor(
        options: GraphQLDataSourceOptions,
        callback: GraphQLDataSourceCallback<T>,
    ) {
        this.client = apolloClient;
        this.options = options;
        this.callback = callback;
    }

    /**
     * Fetch the data from the GraphQL API.
     * It will either call the success or fail callback depending on the result.
     */
    fetchData(params: IServerSideGetRowsParams<T>): void {
        const queryOptions: QueryOptions<OperationVariables, T> = {
            query: this.options.query,
            variables: this.setQueryVariables(params.request),
            fetchPolicy: 'no-cache',
        };

        this.client
            .query<T>(queryOptions)
            .then((response) => {
                const { rows, totalCount } = this.callback(
                    response,
                    queryOptions,
                );

                const rowCount = this.options.infiniteScroll
                    ? this.calculateInfiniteScrollRowCount(
                          rows.length,
                          params.request.startRow ?? 0,
                          params.request.endRow ?? rows.length,
                      )
                    : typeof totalCount === 'number'
                      ? this.calculateRowCount(totalCount)
                      : rows.length;

                // Set up the success arguments for ag-grid
                const successArguments: LoadSuccessParams = {
                    rowData: rows,
                    rowCount,
                    groupLevelInfo: {
                        totalCount,
                    },
                };

                // Call the success callback
                params.success(successArguments);
            })
            .catch((_: unknown) => {
                // Should the query fail, we notify ag-grid of the failure
                params.fail();
            });
    }

    /**
     * Map the ag-grid sort model to what the GraphQL API expects.
     * You can optionally provide a columnSortMap function to map the columnId to the GraphQL field name.
     * The direction is mapped to the GraphQL OrderDirection enum.
     */
    private mapSortModel(
        sortModel: SortModelItem[],
    ): { field: string; direction: string }[] {
        return sortModel.map((srt) => ({
            field:
                typeof this.options.columnSortMap === 'function'
                    ? this.options.columnSortMap(srt.colId)
                    : srt.colId.toUpperCase(),
            direction: mapSortDirection(srt.sort),
        }));
    }

    /**
     * Map the ag-grid filter model to what the GraphQL API expects.
     * You can optionally provide a columnFilterMap function to map the columnId to the GraphQL field name.
     * The filter type is mapped to the GraphQL FilterType enum.
     */
    private mapFilterModel(
        filterModel: FilterModels | null,
    ): ServerSideVariables {
        if (!filterModel) {
            return null;
        }
        // Allow the user to provide a custom filtering mapping function
        if (typeof this.options.columnFilterMap === 'function') {
            return this.options.columnFilterMap(filterModel);
        }

        const whereStatement =
            convertFilterModelToGraphQLWhereStatement(filterModel);
        return mapToObjectRec(whereStatement);
    }

    /**
     * Set the query variables based on the ag-grid request.
     */
    private setQueryVariables(
        request: IServerSideGetRowsRequest,
    ): FilterParameters {
        const { startRow, endRow } = request;

        // Copy the variables from the options
        const variables: FilterParameters = {
            ...this.options.variables,
        };

        // Slice the data based on the ag-grid request
        // (e.g., startRow = 0, endRow = 100 -> first = 100)
        if (
            typeof startRow === 'number' &&
            typeof endRow === 'number' &&
            !this.options.hardLimit
        ) {
            variables.first = endRow - startRow;
        } else if (this.options.hardLimit) {
            variables.first = this.options.hardLimit;
        } else {
            variables.first = 100;
        }

        // Apply the sorting mechanism that GraphQL API expects
        const sortModel = this.mapSortModel(request.sortModel);
        if (sortModel.length > 0) {
            variables.orderBy = sortModel[0]; // TODO: support multiple sort, if possible in the API
        }

        // Apply the filter mechanism that GraphQL API expects
        const where = this.mapFilterModel(request.filterModel);

        if (where && Object.entries(where).length > 0) {
            variables.where = { ...variables.where, ...where };
        }

        return variables;
    }

    private calculateRowCount(totalCount?: number): number {
        return Math.min(this.options.hardLimit ?? Infinity, totalCount ?? 0);
    }

    private calculateInfiniteScrollRowCount(
        rowsReturned: number,
        startRow: number,
        endRow: number,
    ): number | undefined {
        const requested = endRow - startRow;
        if (rowsReturned < requested) {
            return startRow + rowsReturned;
        }
        return undefined as unknown as number;
    }
}
